import { useSyncExternalStore, type ReactNode } from 'react'
import { ArrowUpRight } from '@phosphor-icons/react'
import { CaseStudyCard, NoteCard } from '@/components/home/HomeAiCaseStudies'
import { SeoHead } from '@/components/seo/SeoHead'
import { getAllCaseStudies, getAllNotes } from '@/data/contentService'
import { isPublicCaseStudy } from '@/lib/caseStudyVisibility'
import { ProjectVideo } from '../../design-gosha/site/src/ProjectVideo'
import { ArchiveIcon } from '../../design-gosha/site/src/ArchiveIcon'
import { videoProjects } from '../../design-gosha/site/src/video-projects'
import designContent from '../../design-gosha/site/src/home-content.json'
import archiveContent from '../../design-gosha/site/src/sides-content.json'
import './work.css'

const designOrder = ['iq', 'netflix-streaks', 'kulud.ai', 'battle-pass', 'fff', 'stp', 'less-ms-search', 'united-custom-web', 'betwisio', 'bbet']
const mobileVideoOrder = ['netflix-streaks', 'battle-pass', 'bbet', 'less-ms-search', 'united-custom-web', 'fff'].map(id => `design:${id}`)
const mobileQuery = '(width < 640px)'
function subscribeToMobile(onChange: () => void) {
  if (typeof window.matchMedia !== 'function') return () => {}
  const query = window.matchMedia(mobileQuery)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}
const getMobileSnapshot = () => window.matchMedia?.(mobileQuery).matches ?? false
const getServerSnapshot = () => false
const titles: Record<string, string> = { iq: 'IQ.Space', 'kulud.ai': 'Kulud.ai', stp: 'STP SHOW', betwisio: 'Betwisio' }
const fff = archiveContent.sections.find(project => project.id === 'fff')!
const fffVideo = { title: fff.title, path: 'https://fff.club/', video: '/projects/fff/fff-club-preview.mp4', previewVideo: '/projects/fff/fff-club-preview.preview-v1.mp4', poster: '/projects/fff/fff-club-poster.avif' }

function DesignCard({ id }: { id: string }) {
  const video = id === 'fff' ? fffVideo : videoProjects.find(project => project.id === id)
  const card = designContent.cards.find(project => project.id === id)
  const logo = id === 'fff' ? fff.logo : card?.logo
  const dimensions = id === 'fff' ? { width: 1280, height: 692 } : { width: 1080, height: 1350 }
  if (!video && !card) return null
  const title = video?.title ?? titles[id]
  const href = id === 'less-ms-search' ? 'https://lessms.gosha.ee/' : video?.path ?? card!.href
  const isExternal = id === 'fff' || id === 'less-ms-search'
  return (
    <a className="work-project" href={href} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noopener noreferrer' : undefined}>
      <div className={`work-project-cover${video ? ' work-project-video' : ''}`} style={video ? { aspectRatio: `${dimensions.width} / ${dimensions.height}` } : undefined}>
        {video ? <ProjectVideo project={video} preview {...dimensions} /> : <img {...card!.cover} alt="" loading="lazy" decoding="async" />}
      </div>
      <div className="work-project-meta">
        <div className="work-project-name">
          {logo && <img {...logo} alt="" />}
          <h2>{title}</h2>
        </div>
        <ArrowUpRight size={16} aria-hidden="true" />
      </div>
    </a>
  )
}

export function WorkPage() {
  const isMobile = useSyncExternalStore(subscribeToMobile, getMobileSnapshot, getServerSnapshot)
  const cases = getAllCaseStudies().filter(isPublicCaseStudy).sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
  const notes = [...getAllNotes()].sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
  const writing: { key: string; card: ReactNode }[] = []
  for (let i = 0; i < Math.max(cases.length, notes.length); i++) {
    if (cases[i]) writing.push({ key: `case:${cases[i].page.slug}`, card: <CaseStudyCard cs={cases[i]} fixedHeight={false} tagLimit={4} /> })
    if (notes[i]) writing.push({ key: `note:${notes[i].slug}`, card: <NoteCard note={notes[i]} /> })
  }
  const entries: { key: string; card: ReactNode }[] = []
  for (let i = 0; i < Math.max(designOrder.length, writing.length); i++) {
    if (designOrder[i]) entries.push({ key: `design:${designOrder[i]}`, card: <DesignCard id={designOrder[i]} /> })
    if (writing[i]) entries.push(writing[i])
  }
  // Reorder the actual links too, so keyboard and screen-reader order match the feed.
  const orderedEntries = isMobile
    ? [...mobileVideoOrder.flatMap(key => entries.filter(entry => entry.key === key)), ...entries.filter(entry => !mobileVideoOrder.includes(entry.key))]
    : entries
  return (
    <>
      <SeoHead pathname="/work/" kind="work" />
      <div className="site-page work-page">
        <h1>Work</h1>
        <div className="work-grid">
          {orderedEntries.map(entry => <div className="work-item" key={entry.key}>{entry.card}</div>)}
        </div>
        <a className="work-archive" href="/design/sides/"><ArchiveIcon />Check Archive</a>
      </div>
    </>
  )
}
